import { useAuth } from "@/features/auth";
import {
    useGetPrayerReportMutation,
    useGetPaperlessDepartmentsQuery,
    useGetFacultiesQuery,
    useDownloadPrayerAttachmentsMutation,
    useDownloadPrayerAttachmentsByMeetingAndDepartmentMutation,
} from "./prayer-api";
import { PrayerUiOptions, prayerStatusOptions } from "../prayers-ui-options";
import { PrayerStatuses, Unit } from "@/utils/enums";
import { useGetUpcomingSenateMeeting } from "@/features/meetings";
import { ApiError } from "@/types/common";
import { UserDetails } from "./prayer-utils";
import { fileDownloader } from "@/lib/file-download";
import { Faculty } from "@/features/users";
import { Notify } from "@flexisaf/flexibull2";

export function usePrayerController(formFacultyId?: string, meetingNumber?: string) {
    const { user } = useAuth();

    const userDetails: UserDetails = {
        unit: user.currentRole.unit as Unit,
        faculty: {
            name: user?.department?.faculty?.name ?? user?.faculty?.name,
            id: user?.department?.faculty?.id ?? user?.faculty?.id,
            facultyId: user?.department?.faculty?.facultyId ?? user?.faculty?.facultyId,
        },
        department: {
            name: user?.department?.name,
            id: user?.department?.departmentId,
            departmentId: user?.department?.departmentId,
        },
        roleId: user.currentRole.role.id,
        userId: user.id,
    };

    const [downloadReportReq, downloadPrayerReqMeta] = useGetPrayerReportMutation();
    const [downloadAttachmentsReq, downloadAttachmentsMeta] =
        useDownloadPrayerAttachmentsMutation();
    const [
        downloadAttachmentsByMeetingAndDepartmentReq,
        downloadAttachmentsByMeetingAndDepartmentMeta,
    ] = useDownloadPrayerAttachmentsByMeetingAndDepartmentMutation();
    const { data: faculties, ...getFacultiesMeta } = useGetFacultiesQuery();
    const { data: departments, ...getDepartmentsMeta } = useGetPaperlessDepartmentsQuery(
        // Use formFacultyId if available, otherwise fall back to userDetails.faculty.facultyId
        { facultyId: formFacultyId ?? userDetails.faculty.facultyId ?? "" },
        { skip: !(formFacultyId ?? userDetails.faculty.facultyId) }
    );

    const filteredPrayerStatusOptions = filterPrayerStatusOptions(prayerStatusOptions, user?.unit);

    const facultyOptions = mapFacultiesToOptions(faculties ?? []);
    const departmentOptions = mapPaperlessDepartmentsToOptions(departments);
    const departmentOptionsById = mapPaperlessDepartmentsIdToOptions(departments);
    const { meeting } = useGetUpcomingSenateMeeting();

    // Use meeting number if provided, otherwise fall back to upcoming meeting
    const effectiveMeetingId = meetingNumber ? parseInt(meetingNumber) : meeting?.id;

    const downloadReport = (prayerId: number) => {
        if (!prayerId) return;

        downloadReportReq(prayerId).then((res) => {
            const data = (res as { data: any }).data;
            const filename = `Prayer Report - ${prayerId}`;
            fileDownloader(data, "application/pdf", filename);
        });
    };

    const downloadAttachments = async (prayerId: number) => {
        try {
            const blob = await downloadAttachmentsReq(prayerId).unwrap();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `prayer-${prayerId}-attachments.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            Notify((error as ApiError).message, { status: "error" });
        }
    };

    const downloadAttachmentsByMeetingAndDepartment = async (
        meetingId: number,
        departmentId: string
    ) => {
        const blob = await downloadAttachmentsByMeetingAndDepartmentReq({
            meetingId,
            departmentId,
        }).unwrap();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `prayer-attachments-meeting-${meetingId}-department-${departmentId}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const loading = {
        isLoadingDepartments: getDepartmentsMeta.isLoading,
        isLoadingFaculties: getFacultiesMeta.isLoading,
        isDownloadingReport: downloadPrayerReqMeta.isLoading,
        isDownloadingAttachments: downloadAttachmentsMeta.isLoading,
        isDownloadingAttachmentsByMeetingAndDepartment:
            downloadAttachmentsByMeetingAndDepartmentMeta.isLoading,
    };

    const errors = {
        departmentError: <ApiError>getDepartmentsMeta.error,
        facultyError: <ApiError>getFacultiesMeta.error,
    };

    return {
        userDetails,
        facultyOptions,
        departmentOptions,
        departmentOptionsById,
        filteredPrayerStatusOptions,
        meetingId: effectiveMeetingId,
        downloadReport,
        downloadAttachments,
        downloadAttachmentsByMeetingAndDepartment,
        loading,
        errors,
    };
}

interface Department {
    departmentId: string;
    departmentName: string;
    faculty: Faculty;
    shortName: string;
    id: number;
}

interface PaperlessDepartment {
    id: number;
    departmentId: string;
    name: string;
    abbreviation: string;
    faculty: {
        id: number;
        facultyId: string;
        name: string;
        abbreviation: string;
        organizationId: string;
        createdAt: string;
        updatedAt: string;
    };
    organizationId: string;
    createdAt: string;
    updatedAt: string;
}

function mapFacultiesToOptions(data: Array<Faculty | Department>): PrayerUiOptions[] {
    return data?.map((item) => ({
        label: isDepartment(item) ? item.departmentName : item.name,
        value: isDepartment(item) ? item.id : item.facultyId,
    }));
}

function mapPaperlessDepartmentsToOptions(
    data: PaperlessDepartment[] | undefined
): PrayerUiOptions[] {
    return (
        data?.map((item) => ({
            label: item.name,
            value: item.departmentId, // Use departmentId instead of id to match prayer data structure
        })) ?? []
    );
}
function mapPaperlessDepartmentsIdToOptions(
    data: PaperlessDepartment[] | undefined
): PrayerUiOptions[] {
    return (
        data?.map((item) => ({
            label: item.name,
            value: item.id, // Use departmentId instead of id to match prayer data structure
        })) ?? []
    );
}

function isDepartment(obj: any): obj is Department {
    return "departmentName" in obj;
}

function filterPrayerStatusOptions(prayerStatusOptions, userUnit: Unit) {
    let updatedPrayerStatusOptions = [...prayerStatusOptions];

    if (userUnit === Unit.Faculty) {
        // Remove 'Initiated' from the array
        updatedPrayerStatusOptions = updatedPrayerStatusOptions.filter(
            (option) => option.value !== PrayerStatuses.Initiated
        );
    } else if (userUnit === Unit.Sbc) {
        // Remove 'Initiated' and 'Raised' from the array
        updatedPrayerStatusOptions = updatedPrayerStatusOptions.filter(
            (option) =>
                option.value !== PrayerStatuses.Initiated && option.value !== PrayerStatuses.Raised
        );
    } else if (userUnit === Unit.Secretariat || userUnit === Unit.Senate) {
        // Remove 'Initiated', 'Submitted', and 'Raised' from the array
        updatedPrayerStatusOptions = updatedPrayerStatusOptions.filter(
            (option) =>
                option.value !== PrayerStatuses.Initiated &&
                option.value !== PrayerStatuses.Submitted &&
                option.value !== PrayerStatuses.Raised
        );
    }

    return updatedPrayerStatusOptions;
}
